import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, MessageCircle, Home, Sparkles, Compass, ChevronRight, Building2, Leaf, Moon, Check,
} from 'lucide-react';
import { joinWaitlist } from '../lib/waitlist';
import {
  getLastAssessment, rankDimensions, joinLabels, setReturnTo,
} from '../lib/personalization';

const NAVY = '#0F172A';
const INDIGO = '#4F46E5';
const INDIGO_2 = '#6366F1';
const SLATE_2 = '#6B7280';
const SLATE_3 = '#94A3B8';
const BORDER = '#E2E8F0';
const SOFT_BG = '#F8FAFC';

const WA_NUMBER = '918308595553';

const PREFERENCES = [
  { key: 'luxury',    label: 'Luxury Wellness Resorts',      icon: Building2, color: '#D97706', bg: '#FEF3C7', desc: 'World-class properties designed around your wellbeing.' },
  { key: 'holistic',  label: 'Holistic Wellness Retreats',   icon: Leaf,      color: '#DB2777', bg: '#FCE7F3', desc: 'Ayurveda, yoga and time-tested wellness traditions.' },
  { key: 'mindful',   label: 'Mindfulness & Digital Detox',  icon: Moon,      color: '#6D28D9', bg: '#EDE9FE', desc: 'Screen-free stays for meditation and quiet recovery.' },
];

function scoreBand(score) {
  if (score >= 80) return { label: 'Thriving',    color: '#059669' };
  if (score >= 65) return { label: 'Strong',      color: '#4F46E5' };
  if (score >= 50) return { label: 'Balanced',    color: '#F59E0B' };
  return              { label: 'Needs Attention', color: '#DC2626' };
}

/* ---------------- Personalized state (score exists) ---------------- */
function PersonalizedSpaces({ assessment }) {
  const [pref, setPref] = useState(null);
  const { strongest, weakest } = useMemo(
    () => rankDimensions(assessment.dimension_scores),
    [assessment],
  );
  const band = scoreBand(assessment.total_score);
  const weakLabels = joinLabels(weakest);
  const prefObj = PREFERENCES.find((p) => p.key === pref);

  const message = prefObj
    ? `Hi Clinity, my Wellness Score is ${assessment.total_score}. I'm looking for ${prefObj.label}, especially experiences that can help me improve my ${weakLabels} wellbeing.`
    : `Hi Clinity, my Wellness Score is ${assessment.total_score}. I'd like wellness space recommendations that help me improve my ${weakLabels} wellbeing.`;
  const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;

  const handleGet = () => {
    joinWaitlist({
      source: 'spaces_personalized',
      assessment_id: assessment.assessment_id,
      preference: pref || null,
    });
  };

  return (
    <>
      {/* SCORE + AREAS SUMMARY */}
      <div
        className="rounded-[28px] p-8 md:p-10"
        style={{
          background: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%)',
          border: '1px solid #A7F3D0',
          boxShadow: '0 24px 60px -30px rgba(5,150,105,0.18)',
        }}
        data-testid="spaces-score-summary"
      >
        <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-6 md:gap-10 items-center">
          <div className="flex items-center gap-4 md:pr-8 md:border-r" style={{ borderColor: '#A7F3D0' }}>
            <div
              className="w-[88px] h-[88px] rounded-2xl flex flex-col items-center justify-center"
              style={{ background: '#FFFFFF', border: '1px solid #A7F3D0', boxShadow: '0 8px 24px -12px rgba(5,150,105,0.25)' }}
              data-testid="score-chip"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: SLATE_3 }}>Score</div>
              <div className="text-[30px] font-bold leading-none mt-0.5" style={{ color: NAVY, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>
                {assessment.total_score}
              </div>
              <div className="text-[9px] font-semibold mt-0.5" style={{ color: SLATE_3 }}>/ 100</div>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: band.color }}>{band.label}</div>
              <div className="mt-1 text-[15px] font-semibold" style={{ color: NAVY, fontFamily: 'Outfit, sans-serif' }}>
                Your Wellness Score
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: '#059669' }}>Strongest areas</div>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="dims-strongest">
                {strongest.map((d) => (
                  <span key={d.key} className="px-3 py-1 rounded-full text-[12px] font-semibold bg-white" style={{ color: '#065F46', border: '1px solid #A7F3D0' }}>
                    {d.label} · {d.score}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: '#DC2626' }}>Room to improve</div>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="dims-weakest">
                {weakest.map((d) => (
                  <span key={d.key} className="px-3 py-1 rounded-full text-[12px] font-semibold bg-white" style={{ color: '#991B1B', border: '1px solid #FECACA' }}>
                    {d.label} · {d.score}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PERSONALIZED HEADING */}
      <div className="mt-14 md:mt-20 text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase" style={{ background: '#D1FAE5', color: '#065F46', letterSpacing: '0.22em' }}>
          <Sparkles className="w-3.5 h-3.5" /> Find your fit
        </div>
        <h2
          className="mt-5 text-[30px] sm:text-[40px] lg:text-[48px] font-bold leading-[1.08]"
          style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.028em', color: NAVY }}
          data-testid="spaces-personalized-headline"
        >
          Find a space that fits your wellbeing goals.
        </h2>
        <p
          className="mt-5 text-[15px] sm:text-[17px] leading-[1.65]"
          style={{ color: SLATE_2 }}
          data-testid="spaces-personalized-body"
        >
          We&apos;ll curate options that support your <span style={{ color: NAVY, fontWeight: 600 }}>{weakLabels}</span> wellbeing. Pick a starting point below.
        </p>
      </div>

      {/* 3 PREFERENCES */}
      <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5" data-testid="spaces-preferences">
        {PREFERENCES.map((p) => {
          const Ico = p.icon;
          const selected = pref === p.key;
          return (
            <button
              key={p.key}
              onClick={() => setPref((cur) => (cur === p.key ? null : p.key))}
              data-testid={`pref-${p.key}`}
              aria-pressed={selected}
              className={`group relative text-left rounded-3xl p-6 transition-all duration-300 ${selected ? '-translate-y-1' : 'hover:-translate-y-0.5'}`}
              style={{
                background: '#FFFFFF',
                border: selected ? `2px solid ${p.color}` : `1px solid ${BORDER}`,
                boxShadow: selected ? `0 20px 40px -18px ${p.color}55` : '0 2px 6px rgba(15,23,42,0.04)',
              }}
            >
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110" style={{ background: p.bg }}>
                <Ico className="w-5 h-5" style={{ color: p.color }} strokeWidth={2.1} />
              </div>
              <div className="text-[15px] font-bold leading-tight" style={{ color: NAVY, fontFamily: 'Outfit, sans-serif' }}>
                {p.label}
              </div>
              <div className="mt-2 text-[12.5px] leading-[1.55]" style={{ color: SLATE_2 }}>{p.desc}</div>
              {selected && (
                <div
                  className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: p.color }}
                >
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* CTA */}
      <div className="mt-14 md:mt-16 text-center">
        <div
          className="mx-auto max-w-2xl rounded-[28px] px-8 py-12 md:px-12 md:py-14 relative overflow-hidden bg-white"
          style={{ border: `1px solid ${BORDER}`, boxShadow: '0 40px 80px -30px rgba(15,23,42,0.15)' }}
        >
          <p className="relative text-[12px] font-semibold uppercase tracking-[0.28em]" style={{ color: '#059669' }}>Personalised for you</p>
          <h3
            className="relative mt-3 text-[22px] sm:text-[28px] font-bold leading-tight max-w-lg mx-auto"
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em', color: NAVY }}
          >
            {prefObj
              ? <>Curated {prefObj.label} for <span style={{ color: '#059669' }}>{weakLabels}</span> wellbeing.</>
              : <>Pick a starting point above to get recommendations for <span style={{ color: '#059669' }}>{weakLabels}</span>.</>}
          </h3>
          <div className="relative mt-8">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleGet}
              data-testid="get-wellness-recs-btn"
              className="group inline-flex items-center justify-center gap-3 px-9 py-4 sm:px-11 sm:py-[18px] rounded-full font-semibold text-[15px] sm:text-base text-white no-underline transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
              style={{
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                boxShadow: '0 20px 40px -12px rgba(37,211,102,0.55), inset 0 1px 0 rgba(255,255,255,0.18)',
                letterSpacing: '-0.005em',
              }}
            >
              <MessageCircle className="w-[18px] h-[18px]" strokeWidth={2.2} />
              Get My Recommendations
              <ArrowRight className="w-[18px] h-[18px] transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.2} />
            </a>
            <p className="mt-4 text-xs" style={{ color: SLATE_3 }}>
              Your score, priority areas{prefObj ? ' and preference' : ''} are shared automatically.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------------- Gated state (no score yet) ---------------- */
function GatedSpaces() {
  const navigate = useNavigate();
  const goAssessment = () => {
    setReturnTo('/spaces');
    navigate('/assessment');
  };

  return (
    <div className="text-center max-w-2xl mx-auto">
      <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase" style={{ background: '#D1FAE5', color: '#065F46', letterSpacing: '0.22em' }}>
        <Compass className="w-3.5 h-3.5" /> Start Here
      </div>
      <h1
        className="mt-6 text-[38px] sm:text-[48px] lg:text-[58px] font-bold leading-[1.06]"
        style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em', color: NAVY }}
        data-testid="spaces-gate-headline"
      >
        Know your Wellness Score first.
      </h1>
      <p
        className="mt-6 text-[16px] sm:text-[18px] leading-[1.65]"
        style={{ color: SLATE_2 }}
        data-testid="spaces-gate-body"
      >
        Your Wellness Score tells us which areas of your wellbeing could benefit most — so every space we suggest is aligned with your goals.
      </p>
      <div className="mt-10">
        <button
          onClick={goAssessment}
          data-testid="spaces-gate-cta"
          className="group inline-flex items-center justify-center gap-3 px-9 py-4 sm:px-11 sm:py-[18px] rounded-full font-semibold text-[15px] sm:text-base text-white no-underline transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
          style={{
            background: NAVY,
            boxShadow: '0 18px 40px -14px rgba(15,23,42,0.55)',
            letterSpacing: '-0.005em',
          }}
        >
          Get My Wellness Score
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        <p className="mt-4 text-xs" style={{ color: SLATE_3 }}>Takes about 2 minutes · 13 questions</p>
      </div>
    </div>
  );
}

export default function WellnessSpaces() {
  const navigate = useNavigate();
  const assessment = useMemo(() => getLastAssessment(), []);

  return (
    <div
      className="min-h-screen"
      data-testid="spaces-page"
      style={{ background: `linear-gradient(180deg, ${SOFT_BG} 0%, #FFFFFF 100%)`, color: NAVY, fontFamily: 'Manrope, sans-serif' }}
    >
      <nav className="sticky top-0 z-40 backdrop-blur-xl border-b" style={{ backgroundColor: 'rgba(255,255,255,0.85)', borderColor: BORDER }} data-testid="spaces-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a href="/" className="text-lg font-bold no-underline" style={{ color: NAVY, letterSpacing: '-0.02em', fontFamily: 'Outfit, sans-serif' }}>Clinity</a>
          <button
            onClick={() => navigate('/improve')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 bg-white"
            style={{ border: `1px solid ${BORDER}`, boxShadow: '0 2px 8px rgba(15,23,42,0.06)' }}
            data-testid="spaces-nav-back"
          >
            <ChevronRight className="w-4 h-4 rotate-180" /> Back
          </button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {assessment ? <PersonalizedSpaces assessment={assessment} /> : <GatedSpaces />}
      </main>

      <div className="pb-14 text-center">
        <button
          onClick={() => navigate('/experts')}
          className="text-sm font-semibold inline-flex items-center gap-2 transition-transform hover:translate-x-0.5"
          style={{ color: INDIGO_2 }}
          data-testid="spaces-experts-link"
        >
          Also connect with wellness experts <Home className="w-3.5 h-3.5 -ml-0.5" />
        </button>
      </div>
    </div>
  );
}
